'use client'

import type { ReactNode } from 'react'

import { Icon } from '@/components/ui/icon'

interface Step {
  /** Marker content: a numeral, or 'check' / 'fork' for the two glyph markers. */
  marker: string
  title: string
  description?: string
  children?: ReactNode
}

/**
 * Sun marker with an ink numeral for an ordinary step; teal with a white check
 * for the milestone that closes a phase. The fork marker (step 3) is the same
 * sun fill carrying a split-arrows glyph instead of a number.
 */
function StepMarker({ marker }: { marker: string }) {
  const isCheck = marker === 'check'
  const isGlyph = isCheck || marker === 'fork'

  return (
    <div
      className={
        'grid h-[34px] w-[34px] flex-none place-items-center rounded-full ' +
        (isGlyph ? 'text-base ' : 'text-[13px] font-semibold ') +
        (isCheck
          ? 'bg-[color:var(--cw-lime)] text-white'
          : 'bg-[color:var(--cw-sun)] text-[color:var(--cw-ink)]')
      }
    >
      {isCheck ? (
        <Icon icon="cw-check" aria-hidden="true" />
      ) : marker === 'fork' ? (
        <Icon icon="cw-fork" aria-hidden="true" />
      ) : (
        marker
      )}
    </div>
  )
}

function StepItem({ step }: { step: Step }) {
  return (
    <div className="flex gap-4">
      <StepMarker marker={step.marker} />
      <div className="min-w-0 flex-1 pt-[5px]">
        <div className="font-semibold text-foreground">{step.title}</div>
        {step.description && (
          <p className="mt-0.5 text-[15px] leading-relaxed text-muted">{step.description}</p>
        )}
        {step.children}
      </div>
    </div>
  )
}

/**
 * The steps sit over one continuous 2px rail rather than a connector element
 * between each pair, so the line runs unbroken past the nested fork cards in
 * step 3. `left-4` puts the rail under the centre of the 34px markers.
 */
function StepRail({ children }: { children: ReactNode }) {
  return (
    <div className="relative mt-8">
      <div className="absolute bottom-[18px] left-4 top-[14px] w-[2px] bg-[rgba(28,49,68,.12)]" />
      <div className="relative flex flex-col gap-[26px]">{children}</div>
    </div>
  )
}

function PhaseHeader({ steps, title, subtitle }: { steps: string; title: string; subtitle: string }) {
  return (
    <>
      <div className="flex items-center gap-3.5">
        <span className="text-xs font-semibold uppercase tracking-[.14em] text-[color:var(--cw-teal-btn)]">
          {steps}
        </span>
        <span className="h-px flex-1 bg-[rgba(28,49,68,.14)]" />
      </div>
      <h3 className="cw-display-3 mt-4 text-[30px] text-foreground">{title}</h3>
      <p className="mt-1.5 text-muted">{subtitle}</p>
    </>
  )
}

/** The two "ways forward" cards nested inside step 3. */
function PathCard({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(28,49,68,.14)] bg-[color:var(--cw-paper)] p-[18px]">
      <span className="inline-flex rounded-full border border-[rgba(28,49,68,.24)] px-2.5 py-[5px] text-[11px] font-semibold uppercase tracking-[.1em] text-muted">
        {tag}
      </span>
      <div className="mt-3 font-semibold text-foreground">{title}</div>
      <p className="mt-1 text-[15px] leading-relaxed text-muted">{body}</p>
    </div>
  )
}

export function HowItWorks() {
  return (
    <section className="bg-surface py-[120px]">
      <div className="mx-auto max-w-[1200px] px-6">
        <h2 className="cw-display-2 text-foreground">How It Works</h2>
        <p className="mt-2.5 text-muted">From first contact to finished fleet</p>

        {/* Two phases side by side on desktop, stacked on mobile. */}
        <div className="mt-14 grid grid-cols-1 items-start gap-x-16 gap-y-6 lg:grid-cols-2">
          <div>
            <PhaseHeader
              steps="Steps 1–4"
              title="Phase 1: Design"
              subtitle="From concept to approved graphics"
            />
            <StepRail>
              <StepItem
                step={{
                  marker: '1',
                  title: 'Send Vehicle Photos',
                  description: 'Photos of each vehicle type in your fleet',
                }}
              />
              <StepItem
                step={{
                  marker: '2',
                  title: 'Provide Brand Assets',
                  description: 'Brand guide or finished graphics files',
                }}
              />
              <StepItem
                step={{
                  marker: 'fork',
                  title: '3. Two ways forward',
                  children: (
                    <div className="mt-3 grid gap-3">
                      <PathCard
                        tag="Path A: Ready Graphics"
                        title="Validate & Proceed"
                        body="We verify files, confirm specs, and move to production."
                      />
                      <PathCard
                        tag="Path B: Brand Guide"
                        title="Collaborative Design"
                        body="We create mockups from your brand guide, iterate until approved."
                      />
                    </div>
                  ),
                }}
              />
              <StepItem
                step={{
                  marker: 'check',
                  title: '4. Production',
                  description: 'Graphics printed, laminated, and cut',
                }}
              />
            </StepRail>
          </div>

          <div>
            <PhaseHeader
              steps="Steps 5–9"
              title="Phase 2: Installation"
              subtitle="Vehicle arrives to vehicle leaves branded"
            />
            <StepRail>
              <StepItem
                step={{
                  marker: '5',
                  title: 'Vehicle Arrives',
                  description: 'Drop off or coordinated pickup',
                }}
              />
              <StepItem
                step={{
                  marker: '6',
                  title: 'Inspection',
                  description: 'Surface prep, condition check, documentation',
                }}
              />
              <StepItem
                step={{
                  marker: '7',
                  title: 'Installation',
                  description: 'Professional application by certified installers',
                }}
              />
              <StepItem
                step={{
                  marker: '8',
                  title: 'Quality Check',
                  description: 'Alignment, adhesion, and finish inspection',
                }}
              />
              <StepItem
                step={{
                  marker: 'check',
                  title: '9. Release',
                  description: 'Vehicle ready; on to the next one',
                }}
              />
            </StepRail>
          </div>
        </div>
      </div>
    </section>
  )
}
